/**
 * Step 4: save as a new version — adding to the latest, or replacing it — then
 * what next. With a latest version, the file is compared with its items first
 * (`compareToBase`), so the page says what is already there, what is new and
 * what changed, and refuses a save that would change nothing. With `returnTo`
 * (the new-experiment flow), saving goes straight back there with the dataset.
 */

import { useState } from "react";
import { useNavigate } from "react-router";
import { Button, ButtonLink } from "@/components/ui/button";
import { Field, Option } from "@/components/ui/field";
import { errorText } from "@/components/ui/states";
import { plural } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";
import type { ImportPlan } from "./csv-mapping";
import { useAllDatasetItems, useCreateVersion } from "./hooks";
import { changesAnything, compareToBase, type SaveMode, type VersionChanges } from "./item-identity";
import type { Dataset, DatasetVersion } from "./types";

function Done(props: { dataset: Dataset; saved: DatasetVersion; onAgain: () => void }) {
  const { dataset, saved } = props;
  return (
    <section className="box flex max-w-[640px] flex-col gap-4 p-9">
      <h2 className="text-[22px] font-semibold">
        Saved v{saved.version} · {plural(saved.item_count, "item")}
      </h2>
      <p className="text-sm text-muted">
        Past experiments keep the version they used. New experiments use v{saved.version}.
      </p>
      <div className="flex gap-2.5 pt-2">
        <ButtonLink variant="primary" to={`/experiments/new?dataset=${dataset.id}`}>
          Test an agent with it
        </ButtonLink>
        <ButtonLink to={`/datasets/${dataset.id}`}>View dataset</ButtonLink>
        <Button variant="ghost" onClick={props.onAgain}>
          Upload another
        </Button>
      </div>
    </section>
  );
}

function changeLine(c: VersionChanges, version: number): string {
  const parts = [`${c.unchanged} already in v${version}`, `${c.new} new`, `${c.updated} changed`];
  return [...parts, ...(c.removed ? [`${c.removed} removed`] : [])].join(" · ");
}

/** Why Save is off, or null when it is on. */
function blockReason(
  plan: ImportPlan,
  latest: DatasetVersion | null,
  add: VersionChanges | null,
  replace: VersionChanges | null,
  mode: SaveMode,
) {
  if (plan.items.length === 0) return "Fix the rows in the Check rows step first.";
  if (!latest || !add || !replace) return null;
  const chosen = mode === "add" ? add : replace;
  if (changesAnything(chosen)) return null;
  if (mode === "add" && changesAnything(replace))
    return `Nothing to add: ${add.unchanged === 1 ? "its one item is" : `all ${plural(add.unchanged, "item")} are`} already in v${latest.version}. To keep only this file's items, choose Replace.`;
  return `Nothing changed: this file holds exactly the items in v${latest.version}.`;
}

export function ImportSave(props: {
  dataset: Dataset;
  plan: ImportPlan;
  fileName: string;
  returnTo?: string | null;
  onAgain: () => void;
}) {
  const { dataset, plan, fileName, returnTo } = props;
  const navigate = useNavigate();
  const notify = useUiStore((s) => s.notify);
  const latest = dataset.latest_version;
  const [mode, setMode] = useState<SaveMode>("add");
  const [note, setNote] = useState(`From ${fileName}`);
  const base = useAllDatasetItems(latest ? dataset.id : null, latest?.version ?? null);
  const save = useCreateVersion(dataset.id);

  if (save.data) return <Done dataset={dataset} saved={save.data} onAgain={props.onAgain} />;

  const add = base.data ? compareToBase(base.data, plan.items, "add") : null;
  const replace = base.data ? compareToBase(base.data, plan.items, "replace") : null;
  const chosen = mode === "add" ? add : replace;
  const reason = blockReason(plan, latest, add, replace, mode);
  const comparing = !!latest && base.isPending;
  const total = latest ? (chosen?.total ?? null) : plan.items.length;
  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
      <section className="box flex flex-col gap-5 p-6">
        <Field
          label="Note"
          htmlFor="csv-note"
          hint="Shown in the versions list, so people know what changed."
        >
          <input id="csv-note" className="inp" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {latest && (
          <fieldset className="flex flex-col gap-2">
            <legend className="pb-2 text-[13px] font-semibold">The new version</legend>
            <p
              role="status"
              aria-label={`Compared with v${latest.version}`}
              className="pb-1 text-[13px] text-muted"
            >
              {chosen
                ? changeLine(chosen, latest.version)
                : base.isError
                  ? `Couldn't load v${latest.version} to compare; saving still skips items already in it.`
                  : `Comparing with v${latest.version}…`}
            </p>
            <Option type="radio" name="csv-mode" checked={mode === "add"} onChange={() => setMode("add")}>
              Add new and changed items to v{latest.version} (unchanged ones are skipped)
            </Option>
            <Option
              type="radio"
              name="csv-mode"
              checked={mode === "replace"}
              onChange={() => setMode("replace")}
            >
              Replace all items with this file
            </Option>
          </fieldset>
        )}
        {save.isError && (
          <div role="alert" className="rounded-lg bg-err-bg px-3.5 py-3 text-[13px] text-err">
            {errorText(save.error, "Could not save the items.")}
          </div>
        )}
      </section>
      <aside className="box flex flex-col gap-1 p-5 text-[13px]">
        <h2 className="pb-2 text-[15px] font-semibold">Summary</h2>
        {[
          ["File", fileName],
          ["Items in the file", String(plan.items.length)],
          ["Skipped rows", String(plan.skippedNoInput + plan.skippedRepeats)],
          [
            "Result",
            `${dataset.name} v${(latest?.version ?? 0) + 1}${total === null ? "" : ` · ${plural(total, "item")}`}`,
          ],
        ].map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 border-b border-line-soft py-2 last:border-0">
            <span className="text-muted">{k}</span>
            <span className="text-right font-mono text-xs">{v}</span>
          </div>
        ))}
        {reason && (
          <p id="csv-save-reason" className="pt-2 text-[13px] text-warn">
            {reason}
          </p>
        )}
        <Button
          variant="primary"
          className="mt-3 h-11"
          aria-describedby={reason ? "csv-save-reason" : undefined}
          disabled={!!reason || comparing || save.isPending}
          onClick={() =>
            save.mutate(
              { note: note.trim(), base_version: latest?.version ?? null, mode, items: plan.items },
              {
                onSuccess: (saved) => {
                  if (!returnTo) return;
                  notify(`Saved ${dataset.name} v${saved.version} · ${plural(saved.item_count, "item")}.`);
                  navigate(`${returnTo}${returnTo.includes("?") ? "&" : "?"}dataset=${dataset.id}`);
                },
              },
            )
          }
        >
          {latest ? `Save v${latest.version + 1}` : `Save ${plural(plan.items.length, "item")}`}
        </Button>
      </aside>
    </div>
  );
}
